import { useMemo } from 'react'
import styled from 'styled-components'
import { widgetFont } from '../../widgetTypography'
import { useNavigate } from 'react-router-dom'
import useTranslation from '../../../../hooks/useTranslation'
import AnalyticsCard from './AnalyticsCard'
import { Headline, KpiRow } from './Kpi'
import { rankGroups } from './measures'
import useAnalyticsWidget from './useAnalyticsWidget'

/*
 * A ranking: one row per category, its name on the left, its bar, its figure
 * on the right — customers by sales, suppliers by spend.
 *
 *     breakdown: {
 *         value: 'sales',          a grouped measure
 *         limit: 6,                the top N; with `other` (default) the rest as one row
 *         other: true,
 *         labels: { low: '…' },    translation keys for groups whose value is a code
 *         emptyLabel,              the name of the group with no value
 *         order: 'desc' | 'asc'
 *     }
 *     display    'value' (default) | 'share' | 'both' — what the right column shows
 *     scale      'max' (default) — the longest bar is the largest value;
 *                'share' — bars on a 0–100 % scale, so they read as parts of the total
 *     color      one token for every bar, or `colors: true` for a colour per row
 *     ranks      number the rows
 *     rowPath    '/sales/contact' — a row opens {rowPath}/{group}
 *
 * Plain HTML rather than a chart: the names need to wrap, truncate and carry a
 * tooltip, and the rows need to be links — all of which a list does natively.
 * The bars share one scale, so they stay comparable whatever the card width.
 */

const List = styled.ol`
    list-style: none;
    margin: 0;
    padding: 0;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 0.35rem;
`

const Row = styled.li`
    display: grid;
    grid-template-columns: ${p => p.$ranks ? '1.25rem ' : ''}minmax(5rem, 32%) 1fr auto;
    align-items: center;
    gap: 0.6rem;
    padding: 0.2rem 0.25rem;
    border-radius: 0.375rem;
    ${widgetFont.body}
    cursor: ${p => p.$clickable ? 'pointer' : 'default'};

    &:hover { background: ${p => p.$clickable ? p.theme.table.hover : 'transparent'}; }
    &:focus-visible { outline: 2px solid ${p => p.theme.font.highlight}; outline-offset: -2px; }

    @container analytics (max-width: 320px) {
        grid-template-columns: ${p => p.$ranks ? '1.25rem ' : ''}1fr auto;
        grid-template-areas: ${p => p.$ranks ? '"rank name value" ". bar bar"' : '"name value" "bar bar"'};
        row-gap: 0.2rem;
        & > .rank { grid-area: rank; }
        & > .name { grid-area: name; }
        & > .bar { grid-area: bar; }
        & > .value { grid-area: value; }
    }
`

const Rank = styled.span`
    color: ${p => p.theme.font.label};
    ${widgetFont.caption}
    text-align: right;
    font-variant-numeric: tabular-nums;
`

const Name = styled.span`
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: ${p => p.$muted ? p.theme.font.label : p.theme.font.color};
`

const Track = styled.span`
    position: relative;
    height: 0.6rem;
    border-radius: 999px;
    background: ${p => p.$track};
    overflow: hidden;
`

const Fill = styled.span`
    position: absolute;
    inset: 0 auto 0 0;
    width: ${p => p.$width}%;
    min-width: ${p => p.$width > 0 ? '3px' : 0};
    border-radius: 999px;
    background: ${p => p.$color};
`

const Value = styled.span`
    text-align: right;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;

    small {
        ${widgetFont.caption}
        margin-left: 0.35rem;
        color: ${p => p.theme.font.label};
    }
`

export const groupName = (g, breakdown, t) => {
    if (g.other) return t(breakdown.otherLabel ?? 'analytics.other')
    if (breakdown.labels?.[g.key]) return t(breakdown.labels[g.key])
    return g.name || (g.key ? g.key : t(breakdown.emptyLabel ?? 'analytics.uncategorised'))
}

const HorizontalBarWidget = ({ type, data: config, labels }) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const w = useAnalyticsWidget(type, config)
    const { period, data, palette, format } = w
    const breakdown = config.breakdown ?? {}
    const valueFormat = config.measures?.[breakdown.value]?.format ?? 'number'
    const display = config.display ?? 'value'

    const rows = useMemo(() => {
        if (!data.current) return []
        const groups = data.current.groups(breakdown.value)
        const total = groups.reduce((s, g) => s + g.value, 0)
        const ranked = rankGroups(groups, { limit: breakdown.limit, other: breakdown.other !== false, order: breakdown.order })
        const max = Math.max(...ranked.map(g => Math.abs(g.value)), 0)
        return ranked.map((g, i) => {
            const share = total ? (g.value / total) * 100 : null
            const width = config.scale === 'share' ? Math.max(0, share ?? 0) : max ? (Math.abs(g.value) / max) * 100 : 0
            return { ...g, name: groupName(g, breakdown, t), share, width, color: g.other ? palette.tokens.neutral : config.colors ? palette.color(null, i) : palette.color(config.color ?? 'primary') }
        })
    }, [data.current, breakdown, config.scale, config.colors, config.color, palette, t])

    const open = (row) => config.rowPath && !row.other && row.key && navigate(`${config.rowPath}/${row.key}`)

    const figure = (row) => {
        const value = format(row.value, valueFormat)
        const share = row.share == null ? '—' : format(row.share, 'percent')
        if (display === 'share') return share
        if (display === 'both') return <>{value}<small>{share}</small></>
        return value
    }

    return (
        <AnalyticsCard
            labels={labels}
            period={period}
            path={config.path}
            status={w.status}
            error={data.error}
            onRetry={data.refetch}
            headline={<Headline kpi={w.headline} formatValue={format} compareLabel={w.compareLabel} />}
            footer={<KpiRow kpis={w.kpis} formatValue={format} />}
        >
            <List>
                {rows.map((row, i) => {
                    const clickable = !!config.rowPath && !row.other && !!row.key
                    return (
                        <Row key={row.key} $ranks={config.ranks} $clickable={clickable}
                            tabIndex={clickable ? 0 : undefined} role={clickable ? 'link' : undefined}
                            onClick={() => open(row)} onKeyDown={e => e.key === 'Enter' && open(row)}
                            title={`${row.name}: ${format(row.value, valueFormat)}${row.share != null ? ` (${format(row.share, 'percent')})` : ''}`}>
                            {config.ranks && <Rank className="rank">{row.other ? '' : i + 1}</Rank>}
                            <Name className="name" $muted={row.other}>{row.name}</Name>
                            <Track className="bar" $track={palette.track} aria-hidden="true">
                                <Fill $width={row.width} $color={row.color} />
                            </Track>
                            <Value className="value">{figure(row)}</Value>
                        </Row>
                    )
                })}
            </List>
        </AnalyticsCard>
    )
}

export default HorizontalBarWidget
